import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TableSearchInput as ExternalInput } from '../../../components/_mui';
import { EMAIL, FIRST_NAME, LAST_NAME, USER, USER_ROLE_FILTER_OPTIONS } from '../../../constants/users';
import { useHeaderDispatch } from '../../../contexts/HeaderInfo';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import useSearchFilter from '../../../hooks/useSearchFilter/useSearchFilter';
import useWindowSize from '../../../hooks/useWindowSize';
import { changeUsersActiveView } from '../../../store/actions/shell';
import { selectCurrentOrganizationData } from '../../../store/selectors/organizations';
import { selectUsersActiveView } from '../../../store/selectors/shell';
import { ViewTypesEnum } from '../../../types/shell';
import { IUser } from '../../../types/users';
import Filters from './Filters';

import styles from '../UsersContainer.module.scss';

interface IProps {
  users: IUser[] | null | undefined;
  canEdit: boolean;
  onAdd: () => void;
}

const useFilters = ({ users, canEdit, onAdd }: IProps) => {
  const { mobile } = useWindowSize();
  const { setAdditionalData } = useHeaderDispatch();

  const [roleFilter, setRoleFilter] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const onChangeRef = useRef<(() => void) | undefined>();

  const debouncedOnChange = useDebouncedCallback(() => {
    if (onChangeRef.current) {
      onChangeRef.current();
    }
  }, 500);

  const {
    SearchInput,
    resetSearch,
    query: searchValue,
    filteredItems: filteredBySearch,
  } = useSearchFilter({
    items: users,
    fieldNames: [FIRST_NAME, LAST_NAME, EMAIL],
    ExternalInput,
    onChange: debouncedOnChange,
  });

  const dispatch = useDispatch();
  const activeView = useSelector(selectUsersActiveView);
  const { departmentList: departments } = useSelector(selectCurrentOrganizationData) || {};

  const config = useMemo(
    () => [
      // TODO add status filter after EX-4986 is finished
      {
        label: 'Role',
        tabId: 'role',
        value: roleFilter,
        onChange: setRoleFilter,
        options: USER_ROLE_FILTER_OPTIONS,
        enableSearch: true,
      },
    ],
    [roleFilter],
  );

  const filteredUsers = useMemo(
    () =>
      (filteredBySearch as IUser[])?.filter((user) => {
        const matchesRole = !roleFilter || user.admin_type === roleFilter;
        const matchesDepartment =
          selectedDepartments.length === 0 || selectedDepartments.includes(user.department_id || '');

        return matchesRole && matchesDepartment;
      }),
    [filteredBySearch, roleFilter, selectedDepartments],
  );

  const isAnyFilterActive = useMemo(() => {
    return (
      config.some((filter) => filter?.value?.length > 0) ||
      Boolean(searchValue && searchValue?.length > 0) ||
      selectedDepartments.length > 0
    );
  }, [config, searchValue, selectedDepartments]);

  const renderAddButton = useMemo(
    () =>
      canEdit ? (
        <Button variant="contained" className={styles.addButton} onClick={onAdd}>
          Add {USER}
        </Button>
      ) : null,
    [canEdit, onAdd],
  );

  const handleClearAllFilters = useCallback(() => {
    config.forEach((filter) => {
      filter.onChange('');
    });
    setSelectedDepartments([]);

    resetSearch();
  }, [config, resetSearch]);

  const setActiveView = useCallback(
    (view: ViewTypesEnum) => {
      dispatch(changeUsersActiveView(view));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!setAdditionalData) {
      return;
    }

    setAdditionalData(mobile ? renderAddButton : null);

    // Need to clean additional information after yourself
    return setAdditionalData;
  }, [setAdditionalData, mobile, renderAddButton]);

  const isAnyFilterActiveRef = useRef(isAnyFilterActive);
  const handleClearAllFiltersRef = useRef(handleClearAllFilters);

  isAnyFilterActiveRef.current = isAnyFilterActive;
  handleClearAllFiltersRef.current = handleClearAllFilters;

  const stableOnClear = useCallback(() => {
    if (isAnyFilterActiveRef.current) {
      handleClearAllFiltersRef.current();
    }
  }, []); // No dependencies!

  const stableSearchInputRef = useRef(SearchInput);
  stableSearchInputRef.current = SearchInput;

  const Component = React.useMemo(() => {
    return ({ onChange }: { onChange?: () => void }) => {
      onChangeRef.current = onChange;
      return (
        <Filters
          Search={stableSearchInputRef.current}
          renderAddButton={renderAddButton}
          filterConfig={config}
          activeView={activeView}
          setActiveView={setActiveView}
          onChange={onChange}
          onClear={stableOnClear}
          showClearButton={isAnyFilterActiveRef.current}
          selectedDepartments={selectedDepartments}
          setSelectedDepartments={setSelectedDepartments}
          allDepartments={departments || []}
        />
      );
    };
  }, [activeView, config, renderAddButton, setActiveView, stableOnClear, selectedDepartments, departments]);

  return {
    onClear: handleClearAllFilters,
    filteredUsers,
    activeView,
    searchValue,
    Filters: Component,
  };
};

export default useFilters;
